import { useEffect, useState } from 'react';
import { Badge, Button, Checkbox, Field, NativeSelect, toast } from '@vwt/ui';
import { useApiMutation, useApiQuery } from '../../lib/api.js';
import { toastError } from '../../lib/errors.js';
import { useTranslation } from '../../lib/i18n.js';
import { SLACK_MILESTONES } from '../admin/ops-panels.js';

type Milestone = (typeof SLACK_MILESTONES)[number];
const WORK_EVENTS = SLACK_MILESTONES.filter((m) => m.startsWith('WORK_'));
const REQUEST_EVENTS = SLACK_MILESTONES.filter((m) => m.startsWith('REQUEST_'));
const DEFAULT_EVENTS: Milestone[] = ['WORK_CREATED', 'WORK_DEPLOYED', 'WORK_DELIVERED'];

/**
 * The Slack channel that receives the project's updates and which updates it gets (ADR-0035).
 * Project managers and administrators choose one channel the app can see, or none. It saves on its
 * own, apart from the rest of the project settings.
 */
export function ProjectSlackSettings({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const query = useApiQuery('getProjectSlack', { params: { id: projectId } }, { retry: false });
  const save = useApiMutation('setProjectSlack', { invalidate: ['getProjectSlack'] });
  const view = query.data?.data;
  const current = view?.destinations[0];
  const [channelId, setChannelId] = useState('');
  const [events, setEvents] = useState<Milestone[]>(DEFAULT_EVENTS);
  useEffect(() => {
    setChannelId(current?.channelId ?? '');
    setEvents(current?.milestones.length ? (current.milestones as Milestone[]) : DEFAULT_EVENTS);
  }, [current?.channelId, current?.milestones.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!view?.canManage) return null;
  const channels = view.channels ?? [];
  const selected = channels.find((c) => c.id === channelId);
  const toggle = (m: Milestone, on: boolean) => setEvents((list) => (on ? [...list, m] : list.filter((x) => x !== m)));
  const group = (title: string, list: readonly Milestone[]) => (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-fg-subtle">{title}</span>
      <div className="grid grid-cols-1 gap-x-4 gap-y-1.5 sm:grid-cols-2">
        {list.map((m) => (
          <label key={m} className="flex items-center gap-2 text-sm">
            <Checkbox checked={events.includes(m)} disabled={!channelId} onCheckedChange={(v) => toggle(m, v === true)} />
            {t(`admin.slackDest.milestoneNames.${m}`)}
          </label>
        ))}
      </div>
    </div>
  );

  return (
    <fieldset className="flex flex-col gap-3 rounded-[var(--radius)] border border-border p-3">
      <legend className="px-1 text-sm font-medium">{t('projects.slack.title')}</legend>
      <p className="text-xs text-fg-subtle">{t('projects.slack.hint')}</p>
      {!view.connected ? (
        <p className="text-sm text-fg-muted">{t('projects.slack.notConnected')}</p>
      ) : (
        <>
          {view.channels === null ? <p className="text-xs text-warning">{t('projects.slack.listFailed')}</p> : null}
          <Field label={t('projects.slack.channel')} htmlFor="ps-slack-channel">
            <NativeSelect id="ps-slack-channel" value={channelId} onChange={(e) => setChannelId(e.target.value)}>
              <option value="">{t('projects.slack.none')}</option>
              {current && !channels.some((c) => c.id === current.channelId) ? <option value={current.channelId}>{current.channelName ? `#${current.channelName}` : current.channelId}</option> : null}
              {channels.map((c) => (
                <option key={c.id} value={c.id}>
                  {`#${c.name}${c.isPrivate ? ` (${t('projects.slack.private')})` : ''}`}
                </option>
              ))}
            </NativeSelect>
          </Field>
          {selected && !selected.isMember ? <p className="text-xs text-warning">{t('projects.slack.notMember', { channel: selected.name })}</p> : null}
          {current?.approvedForRestricted && current.channelId === channelId ? <Badge tone="warning">{t('admin.slackDest.restricted')}</Badge> : null}
          {view.destinations.length > 1 ? <p className="text-xs text-fg-subtle">{t('projects.slack.replaces', { count: view.destinations.length })}</p> : null}
          {group(t('projects.slack.workEvents'), WORK_EVENTS)}
          {group(t('projects.slack.requestEvents'), REQUEST_EVENTS)}
          <div className="flex justify-end">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              loading={save.isPending}
              disabled={!!channelId && !events.length}
              onClick={async () => {
                try {
                  await save.mutateAsync({ params: { id: projectId }, body: { channelId: channelId || null, milestones: events.length ? events : DEFAULT_EVENTS } });
                  toast.success(channelId ? t('projects.slack.saved') : t('projects.slack.removed'));
                } catch (error) {
                  toastError(error);
                }
              }}
            >
              {t('projects.slack.save')}
            </Button>
          </div>
        </>
      )}
    </fieldset>
  );
}
